import Button from 'modules/shared-components/button/button';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { valueAsSafeNumber } from 'utils/FormatNumber';
import { useWalletsSearchFilters } from './usePageParams';

import { PrimaryButton } from 'modules/shared-components/button/SubmitButton';
import {
  PresetButton,
  PresetButtonDescription,
  PresetButtonTitle,
} from '../TelegramCalls/PresetButton';
import { chainAsset, Chains } from 'api/types/httpsTypes/d-wallets';
import NumberInput from '../../../components/inputs/number-input';
import CustomModal from '../../../components/modal/CustomModal';
import { useGlobalChain } from 'modules/routing/chainProvider';

type Props = {
  handleClose: () => void;
};
export const schema = yup.object({
  min: yup.number().min(0),
  max: yup.number().min(0),
});

type FormState = yup.InferType<typeof schema>;

export function BalanceFilterModal({ handleClose }: Props) {
  const { filtersState, setFilters } = useWalletsSearchFilters();
  const [selectedChain] = useGlobalChain();
  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
  } = useForm<FormState>({
    resolver: yupResolver(schema),
    defaultValues: {
      min: filtersState.minBalance,
      max: filtersState.maxBalance,
    },
  });

  return (
    <CustomModal title="Balance Range" handleClose={handleClose} showModal>
      <form
        autoComplete="off"
        className="contents"
        onSubmit={(e) => {
          void handleSubmit((form) => {
            setFilters({
              ...filtersState,
              minBalance: form.min || undefined,
              maxBalance: form.max || undefined,
            });
            handleClose();
          })(e);
        }}
      >
        <CustomModal.Body className="xxs:p-4">
          <CustomModal.Title className="hidden lg:flex">
            Balance Range
          </CustomModal.Title>

          <CustomModal.Content>
            <div className="xxs:space-y-3">
              <p className="xxs:text-sm xxs:mb-0">
                The Balance Range filter lets you focus on wallets with a
                specific amount of free {chainAsset(selectedChain)}. This
                feature can help you identify wallets with balances similar to
                yours.
              </p>
              <div className="xxs:space-y-1">
                <p className="xxs:text-sm xxs:mb-0">Set a Range</p>
                <div className="grid grid-cols-3 xxs:gap-1 xxs:mb-4">
                  <PresetButton
                    onClick={() => {
                      setValue('min', undefined);
                      setValue(
                        'max',
                        selectedChain === Chains.Base ||
                          selectedChain === Chains.Ethereum
                          ? 10
                          : selectedChain === Chains.BinanceChain
                          ? 50
                          : 100
                      );
                    }}
                  >
                    <PresetButtonTitle>🐟 Fish</PresetButtonTitle>
                    <PresetButtonDescription>
                      {selectedChain === Chains.Base ||
                      selectedChain === Chains.Ethereum
                        ? '0 to 10 ETH'
                        : selectedChain === Chains.BinanceChain
                        ? '0 to 50 BNB'
                        : '0 to 100 SOL'}
                    </PresetButtonDescription>
                  </PresetButton>
                  <PresetButton
                    onClick={() => {
                      setValue(
                        'min',
                        selectedChain === Chains.Base ||
                          selectedChain === Chains.Ethereum
                          ? 10
                          : selectedChain === Chains.BinanceChain
                          ? 50
                          : 100
                      );
                      setValue(
                        'max',
                        selectedChain === Chains.Base ||
                          selectedChain === Chains.Ethereum
                          ? 50
                          : selectedChain === Chains.BinanceChain
                          ? 250
                          : 500
                      );
                    }}
                  >
                    <PresetButtonTitle>🦐 Shrimp</PresetButtonTitle>
                    <PresetButtonDescription>
                      {selectedChain === Chains.Base ||
                      selectedChain === Chains.Ethereum
                        ? '10 to 50 ETH'
                        : selectedChain === Chains.BinanceChain
                        ? '50 to 250 BNB'
                        : '100 to 500 SOL'}
                    </PresetButtonDescription>
                  </PresetButton>
                  <PresetButton
                    onClick={() => {
                      setValue(
                        'min',
                        selectedChain === Chains.Base ||
                          selectedChain === Chains.Ethereum
                          ? 50
                          : selectedChain === Chains.BinanceChain
                          ? 250
                          : 500
                      );
                      setValue('max', undefined);
                    }}
                  >
                    <PresetButtonTitle>🐳 Whale</PresetButtonTitle>
                    <PresetButtonDescription>
                      Above{' '}
                      {selectedChain === Chains.Base ||
                      selectedChain === Chains.Ethereum
                        ? '50 ETH'
                        : selectedChain === Chains.BinanceChain
                        ? '250 BNB'
                        : '500 SOL'}
                    </PresetButtonDescription>
                  </PresetButton>
                </div>
                <p className="xxs:mb-1">Custom</p>
                <div className="flex xxs:gap-1">
                  <NumberInput
                    label="Min"
                    suffix={chainAsset(selectedChain)}
                    {...register('min', {
                      setValueAs: valueAsSafeNumber,
                    })}
                    error={errors.min?.message}
                  />
                  <NumberInput
                    label="Max"
                    suffix={chainAsset(selectedChain)}
                    {...register('max', {
                      setValueAs: valueAsSafeNumber,
                    })}
                    error={errors.max?.message}
                  />
                </div>
              </div>
            </div>
          </CustomModal.Content>
        </CustomModal.Body>
        <CustomModal.Footer>
          <PrimaryButton type="submit" className="xxs:text-base lg:text-xs">
            SAVE
          </PrimaryButton>
          <Button
            className="xxs:text-base lg:text-xs"
            type="button"
            variant="dexNeutral"
            onClick={() => handleClose()}
          >
            Close
          </Button>
        </CustomModal.Footer>
      </form>
    </CustomModal>
  );
}
